<template>
    <div>
        <div class="main-container text-left">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/geofence', query: { profileId: profileId, serialNumber: serialNumber } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Location Alerts
                </a>
            </div>

            <!-- Page header -->
            <div class="gf-page-header">
                <div>
                    <h1 class="gf-page-header__title">{{ isEditMode ? 'Edit Geofence' : 'New Geofence' }}</h1>
                    <p class="gf-page-header__sub">{{ isEditMode ? 'Update safe zone details' : 'Create a new safe zone' }}</p>
                </div>
            </div>

            <!-- Loading overlay -->
            <div class="gf-loading-overlay" v-if="isLoading">
                <div class="gf-loading-overlay__inner">
                    <i class="fa fa-spinner fa-spin"></i>
                    <span>{{ loadingMessage }}</span>
                </div>
            </div>

            <!-- Form sections -->

            <!-- ── Section: Basic Info ── -->
            <div class="gf-section-label">Basic Info</div>
            <div class="gf-form-card">
                <!-- Name -->
                <div class="gf-field">
                    <label class="gf-field__label">
                        Geofence Name <span class="gf-field__required">*</span>
                    </label>
                    <input
                        ref="geofenceNameInput"
                        type="text"
                        class="gf-field__input"
                        :class="{ 'gf-field__input--error': errors.name }"
                        v-model="currentGeofence.name"
                        placeholder="e.g. Home, School, Grandma's House"
                        maxlength="50"
                    />
                    <span class="gf-field__error" v-if="errors.name">{{ errors.name }}</span>
                    <span class="gf-field__hint" v-else>Give your geofence a descriptive name</span>
                </div>

                <div class="gf-divider"></div>

                <!-- Active toggle -->
                <div class="gf-toggle-row">
                    <div class="gf-toggle-row__info">
                        <span class="gf-toggle-row__title">Geofence Active</span>
                        <span class="gf-toggle-row__sub">Enable or disable this geofence</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="currentGeofence.isActive" />
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>
            </div>

            <!-- ── Section: Location ── -->
            <div class="gf-section-label">Location</div>
            <div class="gf-form-card">
                <!-- Search -->
                <div class="gf-field">
                    <label class="gf-field__label">
                        Search Location <span class="gf-field__required">*</span>
                    </label>
                    <div class="gf-search-wrap">
                        <svg class="gf-search-wrap__icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        <input
                            ref="autocomplete"
                            type="text"
                            class="gf-search-wrap__input"
                            :class="{ 'gf-field__input--error': errors.location }"
                            placeholder="Search for an address or place..."
                        />
                    </div>
                    <span class="gf-field__error" v-if="errors.location">{{ errors.location }}</span>
                    <span class="gf-field__hint" v-else>Search and select, or drag the marker on the map</span>
                </div>

                <!-- Map -->
                <div class="gf-map-wrap">
                    <div class="gf-map-overlay" v-if="isLoadingMap || !googleMapsApiKey || googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY'">
                        <div v-if="isLoadingMap" class="gf-map-placeholder">
                            <i class="fa fa-spinner fa-spin" style="font-size:24px; color:#6b7280;"></i>
                            <span>Loading map...</span>
                        </div>
                        <div v-else class="gf-map-placeholder">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#9ca3af" stroke-width="1.5"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                            <span>Google Maps API key required</span>
                        </div>
                    </div>
                    <div ref="map" class="gf-map"></div>
                </div>
                <span class="gf-field__hint" style="display:block; padding: 6px 0 4px;">
                    Drag the marker or tap the map to adjust. The circle shows the geofence radius.
                </span>

                <div class="gf-divider"></div>

                <!-- Address readonly -->
                <div class="gf-field">
                    <label class="gf-field__label">Detected Address</label>
                    <input
                        type="text"
                        class="gf-field__input gf-field__input--readonly"
                        v-model="currentGeofence.location.address"
                        readonly
                        placeholder="Address will appear after selecting a location"
                    />
                </div>

                <!-- Coordinates -->
                <div class="gf-coords-row">
                    <div class="gf-field" style="flex:1;">
                        <label class="gf-field__label">Latitude</label>
                        <input type="text" class="gf-field__input gf-field__input--readonly"
                            :value="currentGeofence.location.latitude.toFixed(6)" readonly />
                    </div>
                    <div class="gf-field" style="flex:1;">
                        <label class="gf-field__label">Longitude</label>
                        <input type="text" class="gf-field__input gf-field__input--readonly"
                            :value="currentGeofence.location.longitude.toFixed(6)" readonly />
                    </div>
                </div>
            </div>

            <!-- ── Section: Zone ── -->
            <div class="gf-section-label">Zone</div>
            <div class="gf-form-card">
                <!-- Radius -->
                <div class="gf-field">
                    <div class="gf-radius-header">
                        <label class="gf-field__label" style="margin:0;">Radius</label>
                        <span class="gf-radius-val">{{ currentGeofence.radius }}m</span>
                    </div>
                    <input
                        type="range"
                        class="gf-slider"
                        v-model.number="currentGeofence.radius"
                        min="50" max="5000" step="10"
                        @input="updateCircleRadius"
                    />
                    <div class="gf-slider-labels">
                        <span>50m</span><span>2.5km</span><span>5km</span>
                    </div>
                    <div class="gf-radius-warning" v-if="currentGeofence.radius < 200">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 9v4M12 17h.01M10.29 3.86l-8.18 14.18A2 2 0 0 0 3.93 21h16.14a2 2 0 0 0 1.82-2.96L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <span>A radius this small may trigger false entry/exit alerts due to GPS drift. We recommend at least 200m for more reliable notifications.</span>
                    </div>
                </div>
            </div>

            <!-- ── Section: Appearance ── -->
            <div class="gf-section-label">Appearance</div>
            <div class="gf-form-card">
                <!-- Icon -->
                <div class="gf-field">
                    <label class="gf-field__label">Icon</label>
                    <div class="gf-icon-grid">
                        <button
                            v-for="icon in iconOptions"
                            :key="icon.value"
                            type="button"
                            class="gf-icon-btn"
                            :class="{ 'gf-icon-btn--active': currentGeofence.icon === icon.value }"
                            @click="selectIcon(icon.value)"
                        >
                            <i :class="icon.iconClass"></i>
                            <span>{{ icon.label }}</span>
                        </button>
                    </div>
                </div>

                <div class="gf-divider"></div>

                <!-- Color -->
                <div class="gf-field">
                    <label class="gf-field__label">Color</label>
                    <div class="gf-color-grid">
                        <button
                            v-for="color in colorOptions"
                            :key="color"
                            type="button"
                            class="gf-color-btn"
                            :class="{ 'gf-color-btn--active': currentGeofence.color === color }"
                            :style="{ background: color }"
                            @click="selectColor(color)"
                        >
                            <svg v-if="currentGeofence.color === color" width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17l-5-5" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <label class="gf-color-custom">
                            <input type="color" v-model="currentGeofence.color" @change="updateCircleColor" class="gf-color-custom__input" />
                            <span class="gf-color-custom__label">Custom</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- ── Section: Notifications ── -->
            <div class="gf-section-label">Notifications</div>
            <div class="gf-form-card">
                <div class="gf-toggle-row">
                    <div class="gf-toggle-row__info">
                        <div class="gf-toggle-row__icon gf-toggle-row__icon--green">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div>
                            <span class="gf-toggle-row__title">Notify on Entry</span>
                            <span class="gf-toggle-row__sub">Alert when child enters this zone</span>
                        </div>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="currentGeofence.notifyOnEntry" />
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gf-divider"></div>

                <div class="gf-toggle-row">
                    <div class="gf-toggle-row__info">
                        <div class="gf-toggle-row__icon gf-toggle-row__icon--red">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <div>
                            <span class="gf-toggle-row__title">Notify on Exit</span>
                            <span class="gf-toggle-row__sub">Alert when child leaves this zone</span>
                        </div>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="currentGeofence.notifyOnExit" />
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>
            </div>

            <!-- ── Action buttons ── -->
            <div class="gf-actions">
                <button class="gf-save-btn" @click="saveGeofence()" :disabled="isSaving">
                    <span v-if="isSaving"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
                    <span v-else>{{ isEditMode ? 'Update Geofence' : 'Create Geofence' }}</span>
                </button>
                <button class="gf-cancel-btn"
                    @click="$router.push({ path: '/geofence', query: { profileId: profileId, serialNumber: serialNumber } })"
                    :disabled="isSaving">
                    Cancel
                </button>
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import GeofenceService from '@/services/GeofenceService';
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";

export default {
    name: 'CreateGeofence',
    components: { KidHeader, KidFooter },
    data() {
        return {
            profileId: this.$route.query.profileId || '',
            serialNumber: this.$route.query.serialNumber || '',
            geofenceId: this.$route.query.geofenceId || null,
            isEditMode: this.$route.query.edit === 'true',
            userLocation: null,
            userCountry: null,
            userCity: null,
            detectedLocation: false,
            currentGeofence: {
                name: '',
                location: { latitude: 51.5074, longitude: -0.1278, address: '', placeId: null },
                radius: 300,
                icon: 'home',
                color: '#10b981',
                notifyOnEntry: true,
                notifyOnExit: true,
                isActive: true
            },
            map: null, marker: null, circle: null, autocomplete: null, geocoder: null,
            isLoadingMap: true,
            isLoading: false,
            isSaving: false,
            loadingMessage: '',
            errors: {},
            iconOptions: [
                { value: 'home', label: 'Home', iconClass: 'fas fa-home' },
                { value: 'school', label: 'School', iconClass: 'fas fa-school' },
                { value: 'hospital', label: 'Hospital', iconClass: 'fas fa-hospital' },
                { value: 'store', label: 'Store', iconClass: 'fas fa-shopping-cart' },
                { value: 'friend', label: 'Friend', iconClass: 'fas fa-user-friends' },
                { value: 'family', label: 'Family', iconClass: 'fas fa-users' },
                { value: 'other', label: 'Other', iconClass: 'fas fa-map-marker-alt' }
            ],
            googleMapsApiKey: process.env.VUE_APP_GOOGLE_MAP_KEY,
            colorOptions: ['#10b981','#3b82f6','#8b5cf6','#ef4444','#f59e0b','#ec4899','#14b8a6','#6366f1']
        };
    },
    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                if (this.$refs.geofenceNameInput) {
                    this.$refs.geofenceNameInput.focus();
                    if (this.isEditMode) this.$refs.geofenceNameInput.select();
                }
            }, 150);
            if (this.isEditMode && this.geofenceId) {
                this.loadGeofenceForEdit().then(() => { setTimeout(() => this.loadGoogleMaps(), 100); });
            } else {
                setTimeout(() => this.loadGoogleMaps(), 100);
            }
        });
    },
    beforeDestroy() {
        if (this.map) google.maps.event.clearInstanceListeners(this.map);
        if (this.marker) google.maps.event.clearInstanceListeners(this.marker);
    },
    methods: {
        loadGoogleMaps() {
            if (!this.googleMapsApiKey || this.googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
                this.isLoadingMap = false;
                return;
            }
            if (window.google && window.google.maps) { this.initMap(); return; }
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                const check = setInterval(() => { if (window.google && window.google.maps) { clearInterval(check); this.initMap(); } }, 100);
                setTimeout(() => { clearInterval(check); if (!window.google || !window.google.maps) { this.isLoadingMap = false; } }, 10000);
                return;
            }
            setTimeout(() => {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
                script.async = true; script.defer = true;
                script.onload = () => {
                    const check = setInterval(() => { if (window.google && window.google.maps) { clearInterval(check); this.initMap(); } }, 50);
                    setTimeout(() => { clearInterval(check); if (!window.google || !window.google.maps) this.isLoadingMap = false; }, 5000);
                };
                script.onerror = () => { this.isLoadingMap = false; this.$toast.error('Failed to load Google Maps'); };
                document.head.appendChild(script);
            }, 200);
        },
        initMap() {
            if (!this.$refs.map || !window.google || !window.google.maps) { this.isLoadingMap = false; return; }
            const lat = this.currentGeofence.location.latitude;
            const lng = this.currentGeofence.location.longitude;
            this.map = new google.maps.Map(this.$refs.map, { center: { lat, lng }, zoom: 15, mapTypeControl: true, streetViewControl: true, fullscreenControl: true });
            this.geocoder = new google.maps.Geocoder();
            this.marker = new google.maps.Marker({ position: { lat, lng }, map: this.map, draggable: true, animation: google.maps.Animation.DROP });
            this.circle = new google.maps.Circle({ map: this.map, center: { lat, lng }, radius: this.currentGeofence.radius, fillColor: this.currentGeofence.color, fillOpacity: 0.2, strokeColor: this.currentGeofence.color, strokeOpacity: 0.8, strokeWeight: 2 });
            this.circle.bindTo('center', this.marker, 'position');
            this.marker.addListener('dragend', (e) => this.onMarkerDragEnd(e.latLng));
            this.map.addListener('click', (e) => { this.marker.setPosition(e.latLng); this.onMarkerDragEnd(e.latLng); });
            this.initAutocomplete();
            if (!this.currentGeofence.location.address) this.reverseGeocode(this.map.getCenter());
            this.isLoadingMap = false;
        },
        initAutocomplete() {
            if (!this.$refs.autocomplete) return;
            this.autocomplete = new google.maps.places.Autocomplete(this.$refs.autocomplete, { types: ['geocode', 'establishment'], fields: ['geometry', 'formatted_address', 'place_id', 'name'] });
            this.autocomplete.addListener('place_changed', () => {
                const place = this.autocomplete.getPlace();
                if (!place.geometry) { this.$toast.error('No location found'); return; }
                const loc = place.geometry.location;
                this.map.setCenter(loc); this.map.setZoom(16); this.marker.setPosition(loc);
                this.currentGeofence.location.latitude = loc.lat();
                this.currentGeofence.location.longitude = loc.lng();
                this.currentGeofence.location.address = place.formatted_address || place.name;
                this.currentGeofence.location.placeId = place.place_id;
                this.errors.location = '';
            });
        },
        onMarkerDragEnd(latLng) {
            this.currentGeofence.location.latitude = latLng.lat();
            this.currentGeofence.location.longitude = latLng.lng();
            this.reverseGeocode(latLng);
        },
        reverseGeocode(latLng) {
            if (!this.geocoder) return;
            this.geocoder.geocode({ location: latLng }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    this.currentGeofence.location.address = results[0].formatted_address;
                    this.currentGeofence.location.placeId = results[0].place_id;
                }
            });
        },
        updateCircleRadius() { if (this.circle) this.circle.setRadius(this.currentGeofence.radius); },
        updateCircleColor() { if (this.circle) this.circle.setOptions({ fillColor: this.currentGeofence.color, strokeColor: this.currentGeofence.color }); },
        selectIcon(icon) { this.currentGeofence.icon = icon; },
        selectColor(color) { this.currentGeofence.color = color; this.updateCircleColor(); },
        validateForm() {
            this.errors = {};
            if (!this.currentGeofence.name.trim()) this.errors.name = 'Please enter a geofence name';
            if (!this.currentGeofence.location.address) this.errors.location = 'Please select a location';
            return Object.keys(this.errors).length === 0;
        },
        async saveGeofence() {
            if (!this.validateForm()) { this.$toast.error('Please fix the errors'); return; }
            this.isSaving = true;
            try {
                let parentEmail = '';
                try { parentEmail = this.$store?.state?.user?.email || ''; } catch { parentEmail = JSON.parse(localStorage.getItem('user') || '{}').email || ''; }
                const data = { profileId: this.profileId, serialNumber: this.serialNumber, parentEmail, name: this.currentGeofence.name.trim(), location: this.currentGeofence.location, radius: this.currentGeofence.radius, icon: this.currentGeofence.icon, color: this.currentGeofence.color, notifyOnEntry: this.currentGeofence.notifyOnEntry, notifyOnExit: this.currentGeofence.notifyOnExit, isActive: this.currentGeofence.isActive };
                const response = this.isEditMode ? await GeofenceService.updateGeofence(this.geofenceId, data) : await GeofenceService.createGeofence(data);
                if (response.data.success) {
                    //this.$toast.success(this.isEditMode ? 'Geofence updated' : 'Geofence created');
                    setTimeout(() => this.$router.push({ path: '/geofence', query: { profileId: this.profileId, serialNumber: this.serialNumber } }), 500);
                } else {
                    this.$toast.error(response.data.message || 'Failed to save');
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to save geofence');
            } finally {
                this.isSaving = false;
            }
        },
        async loadGeofenceForEdit() {
            this.isLoading = true; this.loadingMessage = 'Loading geofence...';
            try {
                const response = await GeofenceService.getGeofenceById(this.geofenceId, this.profileId, this.serialNumber);
                if (response.data.success) {
                    const g = response.data.data;
                    this.currentGeofence = { name: g.name, location: { latitude: g.location.latitude, longitude: g.location.longitude, address: g.location.address, placeId: g.location.placeId }, radius: g.radius, icon: g.icon, color: g.color, notifyOnEntry: g.notifyOnEntry, notifyOnExit: g.notifyOnExit, isActive: g.isActive };
                }
            } catch {
                this.$toast.error('Failed to load geofence');
                setTimeout(() => this.$router.push({ path: '/geofence', query: { profileId: this.profileId, serialNumber: this.serialNumber } }), 2000);
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #374151;
    text-decoration: none; -webkit-tap-highlight-color: transparent;
}

/* ── Page header ── */
.gf-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 14px 14px; width: 100%; text-align: left;
}
.gf-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.gf-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

/* ── Loading overlay ── */
.gf-loading-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 300; display: flex; align-items: center; justify-content: center;
}
.gf-loading-overlay__inner {
    background: #fff; border-radius: 14px; padding: 20px 28px;
    display: flex; align-items: center; gap: 12px;
    font-size: 14px; font-weight: 500; color: #374151;
}

/* ── Section label ── */
.gf-section-label {
    font-size: 11px; font-weight: 700; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.6px;
    padding: 14px 14px 6px;
    width: 100%;
}

/* ── Form card ── */
.gf-form-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    margin: 0 14px 8px; padding: 14px;
    width: 100%;
}

/* ── Field ── */
.gf-field { display: flex; flex-direction: column; gap: 5px; }
.gf-field__label {
    font-size: 12px; font-weight: 600; color: #374151;
}
.gf-field__required { color: #dc2626; }
.gf-field__input {
    width: 100%; height: 42px;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    padding: 0 12px; font-size: 14px; color: #111;
    background: #fafafa; outline: none;
    box-sizing: border-box;
    -webkit-appearance: none;
}
.gf-field__input:focus { border-color: #2563eb; background: #fff; }
.gf-field__input--error { border-color: #dc2626; }
.gf-field__input--readonly { background: #f9fafb; color: #6b7280; }
.gf-field__error { font-size: 11px; color: #dc2626; }
.gf-field__hint { font-size: 11px; color: #9ca3af; }

/* ── Search wrap ── */
.gf-search-wrap {
    position: relative; display: flex; align-items: center;
}
.gf-search-wrap__icon {
    position: absolute; left: 12px; color: #9ca3af; pointer-events: none;
}
.gf-search-wrap__input {
    width: 100%; height: 42px;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    padding: 0 12px 0 36px; font-size: 14px; color: #111;
    background: #fafafa; outline: none;
    box-sizing: border-box;
}
.gf-search-wrap__input:focus { border-color: #2563eb; background: #fff; }

/* ── Map ── */
.gf-map-wrap {
    position: relative; border-radius: 12px;
    overflow: hidden; border: 0.5px solid #e5e7eb;
    margin: 10px 0 4px; height: 280px;
}
.gf-map { width: 100%; height: 100%; }
.gf-map-overlay {
    position: absolute; inset: 0; background: #f9fafb;
    z-index: 5; display: flex; align-items: center; justify-content: center;
}
.gf-map-placeholder {
    display: flex; flex-direction: column; align-items: center;
    gap: 10px; color: #9ca3af; font-size: 13px;
}

/* ── Coords row ── */
.gf-coords-row { display: flex; gap: 10px; margin-top: 10px; }

/* ── Divider ── */
.gf-divider { height: 0.5px; background: #f3f4f6; margin: 12px 0; }

/* ── Toggle row ── */
.gf-toggle-row { display: flex; align-items: center; gap: 12px; }
.gf-toggle-row__info { flex: 1; display: flex; align-items: center; gap: 10px; }
.gf-toggle-row__icon {
    width: 32px; height: 32px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.gf-toggle-row__icon--green { background: #dcfce7; color: #16a34a; }
.gf-toggle-row__icon--red { background: #fee2e2; color: #dc2626; }
.gf-toggle-row__title { display: block; font-size: 13px; font-weight: 600; color: #111; }
.gf-toggle-row__sub { display: block; font-size: 11px; color: #6b7280; }

/* ── Switch ── */
.kid-action-switch {
    position: relative; display: inline-flex;
    width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0;
}
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track {
    position: absolute; inset: 0;
    background: #d1d5db; border-radius: 999px; transition: background 0.2s;
}
.kid-action-switch__track::after {
    content: ''; position: absolute;
    top: 3px; left: 3px; width: 14px; height: 14px;
    background: white; border-radius: 50%; transition: transform 0.2s;
}
.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Radius slider ── */
.gf-radius-header {
    display: flex; align-items: center;
    justify-content: space-between; margin-bottom: 8px;
}
.gf-radius-val {
    font-size: 14px; font-weight: 700; color: #2563eb;
}
.gf-slider {
    width: 100%; height: 4px; background: #e5e7eb;
    border-radius: 999px; outline: none;
    -webkit-appearance: none; appearance: none; cursor: pointer;
}
.gf-slider::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 20px; height: 20px; background: #2563eb;
    border-radius: 50%; cursor: pointer;
    box-shadow: 0 1px 4px rgba(37,99,235,0.4);
}
.gf-slider::-moz-range-thumb {
    width: 20px; height: 20px; background: #2563eb;
    border-radius: 50%; border: none; cursor: pointer;
}
.gf-slider-labels {
    display: flex; justify-content: space-between;
    font-size: 10px; color: #9ca3af; margin-top: 4px;
}

/* ── Icon grid ── */
.gf-icon-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    gap: 8px; margin-top: 4px;
}
.gf-icon-btn {
    display: flex; flex-direction: column; align-items: center;
    gap: 5px; padding: 10px 6px;
    background: #f9fafb; border: 0.5px solid #e5e7eb;
    border-radius: 10px; cursor: pointer;
    font-size: 11px; color: #6b7280;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.12s;
}
.gf-icon-btn i { font-size: 18px; }
.gf-icon-btn:active { background: #f3f4f6; }
.gf-icon-btn--active {
    background: #eff6ff; border-color: #bfdbfe; color: #2563eb;
}

/* ── Color grid ── */
.gf-color-grid { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-top: 4px; }
.gf-color-btn {
    width: 36px; height: 36px; border-radius: 9px;
    border: 2.5px solid transparent; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: transform 0.12s;
    -webkit-tap-highlight-color: transparent;
}
.gf-color-btn:active { transform: scale(0.92); }
.gf-color-btn--active { border-color: #111; box-shadow: 0 0 0 2px #fff, 0 0 0 4px #111; }
.gf-color-custom {
    display: flex; flex-direction: column; align-items: center;
    gap: 2px; cursor: pointer;
}
.gf-color-custom__input {
    width: 36px; height: 36px; border-radius: 9px;
    border: 0.5px solid #e5e7eb; cursor: pointer; padding: 0;
}
.gf-color-custom__label { font-size: 10px; color: #6b7280; }

/* ── Action buttons ── */
.gf-actions {
    display: flex; flex-direction: column; gap: 10px;
    padding: 8px 14px 24px;
}
.gf-save-btn {
    width: 100%; height: 46px; border-radius: 12px; border: none;
    background: #2563eb; color: #fff;
    font-size: 15px; font-weight: 600; cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s;
}
.gf-save-btn:active { opacity: 0.85; }
.gf-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.gf-cancel-btn {
    width: 100%; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
}
.gf-cancel-btn:disabled { opacity: 0.6; cursor: not-allowed; }

button:focus { outline: none; }

.gf-radius-warning {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 10px;
    padding: 8px 10px;
    background: #fffbeb;
    border: 0.5px solid #fde68a;
    border-radius: 8px;
    font-size: 11px;
    color: #92400e;
}

.gf-radius-warning svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #d97706;
}
</style>